import React, { useMemo, useState } from "react";
import { array, date, number, object, string } from "yup";
import { Formik, FormikConfig, FormikErrors } from "formik";

import { submitHelper } from "../../../helpers/form";
import { StandardModal } from "../../general/Modal/Modal";
import { useToaster } from "../../general/ToastMessages/useToaster";
import { pluralize } from "../../../common/helpers/string";
import { useFinancialAccounts } from "../../../hooks/useFinancialAccounts";
import { useEntities } from "../../../hooks/useEntities";
import { FinancialAccountType } from "../../../common/types/financialAccount";
import { CategoryStructure, useCategories } from "../../../hooks/useCategories";
import { AddTransactionsModalContent } from "./AddTransactionsModalContent";
import { useTransactionsCreation } from "./useTransactionsCreationMutation";

export interface TransactionProxyObject {
    id: string;
    date?: Date;
    description: string;
    amount?: number;
    category: string;
}

interface Props {
    show: boolean;
    onHide: () => void;
}

// Fixing Formik's bad design: https://github.com/jaredpalmer/formik/issues/1251
export const AddTransactionModalMutableConfig = {
    validate: false,
};

export interface AddTransactionsModalFormikConfig {
    isHideAfterSubmit: boolean;
    entityId?: number;

    financialAccountId?: number;
    transactions: TransactionProxyObject[];
}

export type AddTransactionsModalFormErrors =
    FormikErrors<AddTransactionsModalFormikConfig> & {
        noTransactions?: string;
    };

export const NUMBER_OF_CELLS_PER_LINE = 5;

export const COLUMN_INDEX_TO_NAME: Record<
    number,
    keyof TransactionProxyObject
> = {
    1: "date",
    2: "category",
    3: "amount",
    4: "description",
};

const DEFAULT_NUMBER_OF_LINES = 8;

function buildEmptyLines(
    size: number = DEFAULT_NUMBER_OF_LINES,
): TransactionProxyObject[] {
    return new Array(size).fill(null).map(() => ({
        id: Math.random().toString(),
        date: undefined,
        category: "",
        amount: undefined,
        description: "",
    }));
}

const isFilledTransaction = ({
    amount,
    date: transactionDate,
    category,
}: TransactionProxyObject): boolean =>
    !!amount && amount !== 0 && !!transactionDate && !!category;

export const AddTransactionModal: React.FC<Props> = React.memo(
    ({ show, onHide }) => {
        const [loading, setLoading] = useState(false);
        const { toast } = useToaster();
        const [submittedWithErrors, setSubmittedWithErrors] = useState(false);
        const entities = useEntities();

        const save = useTransactionsCreation();

        const categories = useCategories({ structure: CategoryStructure.FLAT });
        const bankAccounts = useFinancialAccounts({
            createTempCashAccounts: true,
        });

        const form: FormikConfig<AddTransactionsModalFormikConfig> = useMemo(
            () => ({
                validateOnBlur: true,
                validateOnChange: true,
                initialValues: {
                    transactions: buildEmptyLines(),
                    isHideAfterSubmit: true,
                },
                enableReinitialize: false,
                validate: (values) => {
                    if (
                        !AddTransactionModalMutableConfig.validate &&
                        !submittedWithErrors
                    ) {
                        return { transactions: undefined };
                    }

                    if (!values.financialAccountId) {
                        if (AddTransactionModalMutableConfig.validate) {
                            setSubmittedWithErrors(true);
                            AddTransactionModalMutableConfig.validate = false;
                        }
                        return {
                            financialAccountId:
                                "Financial Account must be selected",
                        };
                    }

                    const hasAtLeastOneFilledTransaction =
                        values.transactions.some((transaction) =>
                            isFilledTransaction(transaction),
                        );

                    if (!hasAtLeastOneFilledTransaction) {
                        if (AddTransactionModalMutableConfig.validate) {
                            setSubmittedWithErrors(true);
                            AddTransactionModalMutableConfig.validate = false;
                        }
                        return {
                            noTransactions: "Add at least one transaction",
                        };
                    }

                    let hasTransactionError = false;
                    const transactionErrors: Array<
                        | {
                              amount?: string;
                              date?: string;
                              category?: string;
                          }
                        | undefined
                    > = [];

                    const selectedAccount = bankAccounts.find(
                        ({ id }) => id === values.financialAccountId,
                    );

                    // check if all values are filled
                    values.transactions.forEach((transaction) => {
                        if (isFilledTransaction(transaction)) {
                            if (
                                selectedAccount?.entity?.isBusiness !==
                                categories.find(
                                    // eslint-disable-next-line sonarjs/no-nested-functions
                                    ({ id }) => id === transaction.category,
                                )?.isBusiness
                            ) {
                                hasTransactionError = true;
                                transactionErrors.push({
                                    category:
                                        "Assign a category that matches the account type",
                                });
                            } else {
                                transactionErrors.push({});
                            }
                        } else if (
                            !transaction.amount &&
                            !transaction.date &&
                            !transaction.category
                        ) {
                            transactionErrors.push({});
                        } else {
                            hasTransactionError = true;
                            transactionErrors.push({
                                amount: !transaction.amount
                                    ? "Missing amount"
                                    : undefined,
                                date: !transaction.date
                                    ? "Missing date"
                                    : undefined,
                                category: !transaction.category
                                    ? "Missing category"
                                    : undefined,
                            });
                        }
                    });

                    if (hasTransactionError) {
                        if (AddTransactionModalMutableConfig.validate) {
                            setSubmittedWithErrors(true);
                            AddTransactionModalMutableConfig.validate = false;
                        }
                        return { transactions: transactionErrors };
                    }

                    return undefined;
                },
                validationSchema: object().shape({
                    transactions: array().of(
                        object().shape({
                            date: date(),
                            amount: number().nullable(),
                            category: string(),
                            description: string(),
                        }),
                    ),
                }),
                onSubmit: submitHelper({
                    loading,
                    setLoading,
                    handler: async (
                        {
                            transactions,
                            financialAccountId,
                            isHideAfterSubmit,
                            entityId,
                        },
                        { validateForm, resetForm },
                    ) => {
                        const errors = await validateForm({
                            transactions,
                            financialAccountId,
                        });

                        if (Object.keys(errors).length > 0) {
                            setSubmittedWithErrors(true);
                            return;
                        }

                        const transactionsToCreate = transactions
                            .filter((transaction) =>
                                isFilledTransaction(transaction),
                            )
                            .map((transaction) => ({
                                categoryId: transaction.category,
                                date: transaction.date
                                    ? new Date(transaction.date)
                                    : new Date(),
                                amount: transaction.amount ?? 0,
                                memo: transaction.description,
                            }));

                        const result = await save.mutateAsync({
                            transactions: transactionsToCreate,
                            entityId: entityId!,
                            ...(financialAccountId && financialAccountId > 0
                                ? {
                                      financialAccountId,
                                  }
                                : {
                                      createCashAccount: true,
                                  }),
                        });

                        if (result) {
                            const isNotCash =
                                bankAccounts.find(
                                    ({ id }) => id === financialAccountId,
                                )?.financialType !== FinancialAccountType.CASH;

                            let toastContent = "";
                            if (
                                financialAccountId &&
                                financialAccountId > 0 &&
                                isNotCash
                            ) {
                                toastContent = `Added ${transactionsToCreate.length} ${pluralize("transaction", transactionsToCreate.length)} to account ${
                                    bankAccounts.find(
                                        ({ id }) => id === financialAccountId,
                                    )?.name
                                }`;
                            } else {
                                toastContent = `Added ${transactionsToCreate.length} cash ${pluralize("transaction", transactionsToCreate.length)} to entity ${entities.find(({ id }) => id === entityId)?.name}`;
                            }
                            toast(toastContent);
                        }

                        resetForm();
                        if (isHideAfterSubmit) {
                            onHide();
                        }
                        setSubmittedWithErrors(false);
                    },
                }),
            }),
            [
                loading,
                submittedWithErrors,
                save,
                toast,
                entities,
                bankAccounts,
                categories,
                onHide,
            ],
        );

        return (
            <Formik {...form}>
                {({ resetForm }) => (
                    <StandardModal
                        size="xl"
                        onHide={() => {
                            onHide();
                            resetForm();
                        }}
                        show={show}
                    >
                        <AddTransactionsModalContent loading={loading} />
                    </StandardModal>
                )}
            </Formik>
        );
    },
);
